import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Map, Marker } from 'maplibre-gl';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, first, skip } from 'rxjs/operators';
import { MapService } from './map.service';
import { MapDrawerHelperService } from './map-drawer-helper.service';
import { WaypointService } from '../core/waypoint.service';
import { UtilsService } from '../utils/utils.service';
import { SettingsService } from '../account/settings.service';
import { AccountService } from '../account/account.service';
import { LanguageService } from '../account/language.service';
import { MobileResolutionService } from '../platform/mobile-resolution.service';
import { WidgetService } from '../platform/widget.service';
import { ChargingStationDetails, ChargerProvider, Plug, PlugPowerSplit, carPlugPower, plugPowerSplit } from 'src/app/models/charging-station';
import { Route, RoutePlanElement, RoutePlanType } from 'src/app/models/route';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DisplayMode } from 'src/app/models/display-mode';
import { SvgRecolorService } from '../media/svg-recolor.service';
import { VehicleService } from '../vehicle/vehicle.service';

export const enum PopupAlign {
  Left,
  Right,
  Top
}

// Material icons (power, directions_car) for the plug power chips; inline so they take the
// chip's text colour in both skins.
const CHARGER_ICON_PATH = 'M16.01 7L16 3h-2v4h-4V3H8v4h-.01C7 6.99 6 7.99 6 8.99v5.49L9.5 18v3h5v-3l3.5-3.51v-5.5c0-1-1-2-1.99-1.99z';
const CAR_ICON_PATH = 'M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z';

/** Layout and flags of a charging station popup. */
export interface ChargingPopupOptions {
  align: PopupAlign;
  widthAnchor: number;
  heightAnchor: number;
  statusSelector: boolean;
  planBtn: boolean;
  searched: boolean;
}

/** Parameters of {@link MapChargingPopupService.loadChargingStationPopup}. */
export interface LoadChargingPopupOptions extends ChargingPopupOptions {
  chargingStationId: string;
  fallbackCoords?: { lat: number, lon: number };
}

/** Parameters of the popup draw: the layout plus the already fetched station. */
export interface DrawChargingPopupOptions extends ChargingPopupOptions {
  chargingStation: ChargingStationDetails;
  /** Pan the map to reveal the popup; defaults to true. */
  recenter?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MapChargingPopupService {

  private mapInstance: Map | null = null;
  private lastOpenedChargingStationPopup: ChargingStationDetails | null = null;
  // Layout and flags of the last draw, so the popup can be rebuilt in place.
  private lastPopupLayout: ChargingPopupOptions | null = null;
  private chargingStationPopupMarker: Marker | null = null;
  private chargerPopupSubscriptions: Subscription[] = [];
  private lastRouteData: Route | null = null;

  public statusChanged$ = new Subject<void>();

  private readonly mapService = inject(MapService);
  private readonly mapDrawerHelperService = inject(MapDrawerHelperService);
  private readonly waypointService = inject(WaypointService);
  private readonly utilsService = inject(UtilsService);
  private readonly settingsService = inject(SettingsService);
  private readonly accountService = inject(AccountService);
  private readonly languageService = inject(LanguageService);
  private readonly mobileResolutionService = inject(MobileResolutionService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly svgRecolorService = inject(SvgRecolorService);
  private readonly widgetService = inject(WidgetService);
  private readonly vehicleService = inject(VehicleService);

  constructor() {
    // The popup markup bakes the skin in: applySvgRecolors() reads a skin-bound
    // CSS variable at draw time and the icon paths carry the skin folder. A popup
    // left open across a skin switch would keep the old colors, so rebuild it.
    // ObservableMap also emits on the POI toggle; only a real skin change counts.
    // setMap() flips the body class before emitting, so the redraw already sees
    // the new skin's variables.
    this.settingsService.ObservableMap.pipe(
      distinctUntilChanged((prev, next) => prev?.Skin === next?.Skin),
      skip(1),
      takeUntilDestroyed()
    ).subscribe(() => this.redrawOpenedPopupForSkin());
  }

  public init(map: Map): void {
    this.mapInstance = map;
  }

  public destroy(): void {
    this.chargerPopupSubscriptions.forEach(s => s.unsubscribe());
    this.chargerPopupSubscriptions = [];
    this.removeChargingStationPopup();
    this.mapInstance = null;
  }

  public getLastOpenedPopup(): ChargingStationDetails | null {
    return this.lastOpenedChargingStationPopup;
  }

  public setLastRouteData(routeData: Route | null): void {
    this.lastRouteData = routeData;
  }

  // ─── Charging Station Popup ──────────────────────────────────────────

  /**
   * Fetches the station details and opens the popup. `fallbackCoords` is the
   * position the caller already knows (clicked feature, route element, ...); it
   * anchors the popup when the backend answers without coordinates, e.g. with a
   * bare `{ id }` for a station it has no record of.
   */
  public loadChargingStationPopup({ align, widthAnchor, heightAnchor, chargingStationId, statusSelector, planBtn, searched,
    fallbackCoords }: LoadChargingPopupOptions): void {
    this.chargerPopupSubscriptions.forEach(s => s.unsubscribe());
    this.chargerPopupSubscriptions = [];

    this.mapService.getChargingStationDetail([chargingStationId]).subscribe((resp) => {
      const chargingStation = this.withDefaults(resp?.[0], chargingStationId, fallbackCoords);
      if (chargingStation) {
        this.drawChargingStationPopup({ align, widthAnchor, heightAnchor, chargingStation, statusSelector, planBtn, searched });
      }
    });
  }

  // Fills in whatever the detail response left out so the popup can always be
  // drawn with default content. Returns null only when no position is known at
  // all, because a marker cannot be placed without one.
  private withDefaults(details: ChargingStationDetails | undefined, chargingStationId: string,
    fallbackCoords?: { lat: number, lon: number }): ChargingStationDetails | null {
    const lat = Number.isFinite(details?.lat) ? details!.lat : fallbackCoords?.lat;
    const lon = Number.isFinite(details?.lon) ? details!.lon : fallbackCoords?.lon;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

    return {
      id: details?.id ?? chargingStationId,
      provider: details?.provider ?? this.mapService.getChargerProviderById(chargingStationId),
      lat: lat!,
      lon: lon!,
      plugs: details?.plugs ?? [],
      img: details?.img ?? '',
      opid: details?.opid ?? -1,
      cost: details?.cost ?? '',
      email: details?.email ?? '',
      phone: details?.phone ?? '',
      title: details?.title || this.languageService.languageJSON.Global_ChargingStation || 'Charging Station',
    };
  }

  private drawChargingStationPopup({ align, widthAnchor, heightAnchor, chargingStation, statusSelector, planBtn, searched,
    recenter = true }: DrawChargingPopupOptions): void {
    // Widget `stationlists=0`: no favorite / disabled station lists, so no selector to fill them.
    statusSelector = statusSelector && this.widgetService.stationListsEnabled();
    this.lastOpenedChargingStationPopup = chargingStation;
    this.lastPopupLayout = { align, widthAnchor, heightAnchor, statusSelector, planBtn, searched };
    this.removeChargingStationPopup();

    const map = this.mapInstance!;

    let tooltip = this.getTooltipOpeningHtml(align, planBtn, searched);

    // Title - clean address text
    tooltip += `<div class="cs-title">${this.escapeHtml(chargingStation.title)}</div>`;

    tooltip += `<div class="body">`;

    // Operator & info card
    tooltip += this.getInfoCardHtml(chargingStation);

    // Plug card
    tooltip += this.getPlugCardHtml(chargingStation);

    // Status selector
    if (statusSelector) {
      tooltip += this.getStatusSelectorHtml(chargingStation.id);
    }
    tooltip += `</div>`;

    tooltip += this.getPopupButtonsHtml(planBtn, searched);
    tooltip += "</div>";

    // Create a 0x0 anchor element so MapLibre's translate(-50%, -50%) has no effect
    const popupEl = document.createElement('div');

    // Position the tooltip via an absolutely-positioned wrapper
    const tooltipWrapper = document.createElement('div');
    tooltipWrapper.innerHTML = tooltip;
    tooltipWrapper.style.position = 'absolute';
    this.positionTooltipWrapper(tooltipWrapper, align, widthAnchor, heightAnchor);

    popupEl.appendChild(tooltipWrapper);

    this.chargingStationPopupMarker = new Marker({ element: popupEl })
      .setLngLat([chargingStation.lon, chargingStation.lat])
      .addTo(map);

    this.applySvgRecolors(tooltipWrapper);
    this.mapDrawerHelperService.cardOpened(popupEl);
    if (recenter) {
      // On the wide layout the planning side panel covers the left of the map
      // in route mode, so nudge the popup towards the right half to keep it
      // clear. The compact layout hides the panel while a card is open instead.
      const shiftX = !this.mobileResolutionService.getMobileResolution()
        && this.waypointService.getSelectedDisplayMode() == DisplayMode.ROUTE ? 125 : 0;
      const tooltipEl = tooltipWrapper.querySelector<HTMLElement>('.map-tooltip') ?? tooltipWrapper;
      this.mapDrawerHelperService.revealCard([chargingStation.lon, chargingStation.lat], tooltipEl, { shiftX });
    }

    // Bind event listeners after DOM is ready
    setTimeout(() => {
      this.bindPopupEventListeners(chargingStation, statusSelector, planBtn);
    });
  }

  // ─── Popup HTML Builders ─────────────────────────────────────────────

  private getTooltipOpeningHtml(align: PopupAlign, planBtn: boolean, searched: boolean): string {
    switch (align) {
      case PopupAlign.Left:
        return "<div class='map-tooltip arrow-to-right cs-tooltip ev-card'>";
      case PopupAlign.Right:
        return "<div class='map-tooltip arrow-to-left cs-tooltip ev-card'>";
      case PopupAlign.Top:
        if (planBtn || searched) {
          return "<div class='map-tooltip arrow-to-bottom top cs-tooltip ev-card'>";
        }
        return "<div class='map-tooltip arrow-to-bottom top rounded-body cs-tooltip ev-card'>";
      default:
        return "";
    }
  }

  private getOperatorInfo(opid: number): { name: string, url: string } {
    if (opid != undefined && opid != null && opid != -1) {
      const operator = this.mapService.getChargingOperatorByID(opid);
      if (operator) {
        return { name: operator.name, url: operator.url || '' };
      }
    }
    return { name: '', url: '' };
  }

  private getInfoCardHtml(chargingStation: ChargingStationDetails): string {
    const { name: operatorName, url: operatorUrl } = this.getOperatorInfo(chargingStation.opid);

    const hasInfo = operatorName || chargingStation.phone || chargingStation.cost || chargingStation.provider == ChargerProvider.OCM;
    if (!hasInfo) {
      return '';
    }

    let html = `<div class="cs-info-card">`;

    if (operatorName) {
      html += `<div class="cs-operator-name">${this.escapeHtml(operatorName)}</div>`;
    }

    if (operatorUrl) {
      html += `<div class="cs-info-row">
          <span class="cs-info-icon" data-recolor-src="assets/icons/dark/map/info_icon_web.svg"></span>
          <a target="_blank" href="${this.escapeHtml(operatorUrl)}">${this.escapeHtml(operatorUrl)}</a></div>`;
    }

    if (chargingStation.phone) {
      html += `<div class="cs-info-row">
          <img class="cs-info-icon" src="assets/icons/${this.settingsService.getMap().Skin}/map/info_icon_phone.svg" />
          <a target="_blank" href="tel:${this.escapeHtml(chargingStation.phone)}">${this.escapeHtml(chargingStation.phone)}</a></div>`;
    }

    if (chargingStation.cost) {
      html += this.getCostRowHtml(chargingStation.cost);
    }

    if (chargingStation.provider == ChargerProvider.OCM) {
      const ocmId = this.utilsService.getOCMId(chargingStation.id);
      html += `<div class="cs-info-row">
          <img class="cs-info-icon" src="assets/icons/default/map/info_icon_ocm.svg" />
          <a target="_blank" href="https://openchargemap.org/site/poi/details/${this.escapeHtml(ocmId)}">${this.languageService.languageJSON.Map_Popup_MoreInfo}</a></div>`;
    }

    html += `</div>`;
    return html;
  }

  private getCostRowHtml(cost: string): string {
    if (this.utilsService.validURL(cost)) {
      return `<div class="cs-info-row">
            <img class="cs-info-icon" src="assets/icons/${this.settingsService.getMap().Skin}/map/info_icon_price.svg" />
            <a target="_blank" href="${this.escapeHtml(cost)}">${this.escapeHtml(cost)}</a></div>`;
    }
    return `<div class="cs-info-row">
            <img class="cs-info-icon" src="assets/icons/${this.settingsService.getMap().Skin}/map/info_icon_price.svg" />
            <span>${this.escapeHtml(cost)}</span></div>`;
  }

  private getPlugCardHtml(chargingStation: ChargingStationDetails): string {
    const plugs = chargingStation.plugs ?? [];
    if (plugs.length == 0) {
      return '';
    }
    let html = `<div class="cs-plug-card">`;
    for (const plug of plugs) {
      html += this.getPlugRowHtml(plug);
    }
    html += `</div>`;
    return html;
  }

  private getPlugRowHtml(plug: Plug): string {
    const chargePower = Math.round(plug.power * 10) / 10;
    const carPower = Math.round(carPlugPower(plug.type, this.vehicleService.getSelectedCar()) * 10) / 10;
    const split = plugPowerSplit(chargePower, carPower);

    let html = `<div class="cs-plug-row">
          <div class="charger-icon"><span data-recolor-src="/assets/icons/dark/plug/${this.utilsService.chargingstationsImg[plug.type - 1][0]}"></span>`;

    if (this.accountService.getUserSubscriptionType().includes('proplus.') && (plug.statusCount ?? 0) > 0) {
      html += `<div class="cs-availability ${plug.available! > 0 ? 'available' : 'not-available'}">${plug.available}/${plug.statusCount}</div>`;
    }

    html += `</div>
          <div class="cs-plug-info">
            <span class="cs-plug-text">${plug.count} x ${this.utilsService.chargingstationsImg[plug.type - 1][1]}</span>
            ${this.getPowerChipsHtml(split, chargePower, carPower)}
          </div></div>`;
    return html;
  }

  // Charger max and car max side by side; the one the car actually charges at is highlighted
  // (both when they match). Without a known car limit only the charger chip shows.
  private getPowerChipsHtml(split: PlugPowerSplit, chargePower: number, carPower: number): string {
    const lang = this.languageService.languageJSON;
    let html = `<div class="cs-power-chips">`
      + this.getPowerChipHtml(CHARGER_ICON_PATH, lang.Map_Popup_Charger, chargePower, split.limitedBy != 'car');
    if (split.limitedBy != 'unknown') {
      html += this.getPowerChipHtml(CAR_ICON_PATH, lang.Map_Popup_YourCar, carPower, split.limitedBy != 'charger');
    }
    return html + `</div>`;
  }

  private getPowerChipHtml(iconPath: string, label: string, power: number, active: boolean): string {
    return `<span class="cs-power-chip${active ? ' active' : ''}" title="${label} ${power} kW" aria-label="${label} ${power} kW">`
      + `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${iconPath}"/></svg>${power} kW</span>`;
  }

  private getStatusSelectorHtml(chargingStationId: string): string {
    let html = `<div class="cs-status-wrapper">`;
    html += `<div class="cs-status-card status status-opener">`;
    if (this.settingsService.getContainsDisabledChargingStationId(chargingStationId)) {
      html += this.getChargerDisabledStatusHtmlElementsSelected();
    } else if (this.settingsService.getContainsFavoriteChargingStationId(chargingStationId)) {
      html += this.getChargerFavoriteStatusHtmlElementsSelected();
    } else {
      html += this.getChargerNormalStatusHtmlElementsSelected();
    }
    html += `<img class="status-opener-icon" src="assets/icons/${this.settingsService.getMap().Skin}/ui/open_arrow.svg" /></div>`;

    html += `<div class="status-dropdown">
        <div class="status" id="normal-btn">${this.getChargerNormalStatusHtmlElements()}</div>
        <div class="divider"></div>
        <div class="status" id="favorite-btn">${this.getChargerFavoriteStatusHtmlElements()}</div>
        <div class="divider"></div>
        <div class="status" id="disabled-btn">${this.getChargerDisabledStatusHtmlElements()}</div>
      </div>`;
    html += `</div>`;
    return html;
  }

  private getPopupButtonsHtml(planBtn: boolean, searched: boolean): string {
    let html = '';
    if (planBtn) {
      if (this.waypointService.getWaypointsParams().length >= 5) {
        html += "<div class='ev-button primary plan-btn inactive'>" + this.languageService.languageJSON.Map_Popup_ChargeHere + "</div>";
      } else {
        html += "<div class='ev-button primary plan-btn'>" + this.languageService.languageJSON.Map_Popup_ChargeHere + "</div>";
      }
    }
    if (searched) {
      html += "<div class='plan-btn plan-btn-container'></div>";
    }
    return html;
  }

  private positionTooltipWrapper(tooltipWrapper: HTMLElement, align: PopupAlign, widthAnchor: number, heightAnchor: number): void {
    switch (align) {
      case PopupAlign.Left:
        tooltipWrapper.style.right = `${Math.abs(widthAnchor)}px`;
        tooltipWrapper.style.transform = 'translateY(-50%)';
        break;
      case PopupAlign.Right:
        tooltipWrapper.style.left = `${Math.abs(widthAnchor)}px`;
        tooltipWrapper.style.transform = 'translateY(-50%)';
        break;
      case PopupAlign.Top:
        tooltipWrapper.style.bottom = `${heightAnchor}px`;
        tooltipWrapper.style.transform = 'translateX(-50%)';
        break;
    }
  }

  private applySvgRecolors(root: HTMLElement): void {
    const color = getComputedStyle(document.body)
      .getPropertyValue('--gradient-color-over-gradient').trim();

    root.querySelectorAll<HTMLElement>('[data-recolor-src]').forEach(placeholder => {
      const src = placeholder.dataset['recolorSrc']!;
      const cls = placeholder.getAttribute('class') ?? '';
      this.chargerPopupSubscriptions.push(
        this.svgRecolorService.recolor(src, { color }).subscribe(svgMarkup => {
          placeholder.outerHTML = svgMarkup.replace('<svg', `<svg class="${cls}"`);
        })
      );
    });
  }

  private bindPopupEventListeners(chargingStation: ChargingStationDetails, statusSelector: boolean, planBtn: boolean): void {
    if (planBtn) {
      this.bindPlanButtonListener(chargingStation);
    }

    if (statusSelector) {
      this.bindStatusSelectorListeners(chargingStation.id);
    }
  }

  private bindPlanButtonListener(chargingStation: ChargingStationDetails): void {
    if (this.waypointService.getWaypointsParams().length >= 5) {
      this.snackBar.open(this.languageService.languageJSON.InfoSidebar_WaypointLimitReached, "",
        { duration: 5000, panelClass: 'snackbar-info' });
    }

    const planBtnEl = document.getElementsByClassName('plan-btn')[0];
    if (planBtnEl) {
      this.chargerPopupSubscriptions.push(
        fromEvent(planBtnEl, 'click').pipe(first()).subscribe((event: any) => {
          if (event.isTrusted) {
            this.removeChargingStationPopup();
            this.waypointService.addChargingStationWaypoint(
              chargingStation.title,
              chargingStation.lat,
              chargingStation.lon,
              chargingStation.id,
              this.getChargeHereClosestRouteIndex(chargingStation)
            );
          }
        })
      );
    }
  }

  private bindStatusSelectorListeners(chargingStationId: string): void {
    const openerEl = document.getElementsByClassName('status-opener')[0];
    const dropdownEl = document.getElementsByClassName('status-dropdown')[0];
    const tooltipEl = document.getElementsByClassName('cs-tooltip')[0];
    const normalBtn = document.getElementById('normal-btn');
    const favoriteBtn = document.getElementById('favorite-btn');
    const disabledBtn = document.getElementById('disabled-btn');

    if (openerEl) {
      this.chargerPopupSubscriptions.push(fromEvent(openerEl, 'click').subscribe((e: any) => {
        if (e.isTrusted && dropdownEl) { dropdownEl.classList.add("show"); }
      }));
    }
    if (dropdownEl) {
      this.chargerPopupSubscriptions.push(fromEvent(dropdownEl, 'click').subscribe((e: any) => {
        if (e.isTrusted) { dropdownEl.classList.remove("show"); }
      }));
    }
    if (tooltipEl) {
      this.chargerPopupSubscriptions.push(fromEvent(tooltipEl, 'click').subscribe((e: any) => {
        if (e.isTrusted && !this.isStatusSelectorClick(e) && dropdownEl) { dropdownEl.classList.remove("show"); }
      }));
    }
    this.bindStatusButton(normalBtn, () => {
      this.settingsService.setToNormalChargingStation(chargingStationId);
      this.changeStatusOpener(this.getChargerNormalStatusHtmlElementsSelected());
    });
    this.bindStatusButton(favoriteBtn, () => {
      this.settingsService.setToFavoriteChargingStation(chargingStationId);
      this.changeStatusOpener(this.getChargerFavoriteStatusHtmlElementsSelected());
    });
    this.bindStatusButton(disabledBtn, () => {
      this.settingsService.setToDisabledChargingStation(chargingStationId);
      this.changeStatusOpener(this.getChargerDisabledStatusHtmlElementsSelected());
    });
  }

  // True when the click landed on the status opener or inside the dropdown,
  // which handle the dropdown's visibility themselves.
  private isStatusSelectorClick(e: any): boolean {
    const path = e.composedPath();
    if (!path) {
      return false;
    }
    for (const el of path) {
      if (el.className?.includes("status status-opener") ||
        el.className?.includes("status-dropdown")) {
        return true;
      }
    }
    return false;
  }

  private bindStatusButton(btn: HTMLElement | null, onClick: () => void): void {
    if (btn) {
      this.chargerPopupSubscriptions.push(fromEvent(btn, 'click').subscribe((e: any) => {
        if (e.isTrusted) { onClick(); }
      }));
    }
  }

  public removeChargingStationPopup(): void {
    if (this.chargingStationPopupMarker) {
      this.mapDrawerHelperService.cardClosed(this.chargingStationPopupMarker.getElement());
      this.chargingStationPopupMarker.remove();
      this.chargingStationPopupMarker = null;
    }
    this.chargerPopupSubscriptions.forEach(s => s.unsubscribe());
    this.chargerPopupSubscriptions = [];
  }

  // Re-draws the currently open charging-station popup (if any) so its imperatively
  // built HTML picks up newly loaded translation texts. No-op when no popup is visible.
  public redrawOpenedChargingStationPopup(): void {
    if (this.chargingStationPopupMarker && this.lastOpenedChargingStationPopup) {
      this.redrawChargingStationPopup(this.lastOpenedChargingStationPopup);
    }
  }

  // Rebuilds the open popup after a skin change with the exact layout it was
  // drawn with (a route charger popup opens Left/Right-aligned, which the
  // generic redraw above would turn into a Top one) and without panning the
  // map, since the user is mid-settings and the popup hasn't moved.
  private redrawOpenedPopupForSkin(): void {
    if (!this.mapInstance || !this.chargingStationPopupMarker ||
      !this.lastOpenedChargingStationPopup || !this.lastPopupLayout) {
      return;
    }
    const layout = this.lastPopupLayout;
    this.drawChargingStationPopup({
      align: layout.align, widthAnchor: layout.widthAnchor, heightAnchor: layout.heightAnchor,
      chargingStation: this.lastOpenedChargingStationPopup, statusSelector: layout.statusSelector,
      planBtn: layout.planBtn, searched: layout.searched, recenter: false
    });
  }

  public redrawChargingStationPopup(chargingStationPopup: ChargingStationDetails): void {
    let chargerPlannedRouteOccurence: number = 0;
    if (this.lastRouteData) {
      const routeChargers: RoutePlanElement[] = this.lastRouteData.RoutePlan.filter((routePlanEl) => {
        return routePlanEl.Type == RoutePlanType.Charger;
      });
      chargerPlannedRouteOccurence = routeChargers.filter((routeCharger) =>
        routeCharger.OperatorId == chargingStationPopup.id
      ).length;
    }
    this.removeChargingStationPopup();
    const favoriteCharger = this.settingsService.getContainsFavoriteChargingStationId(chargingStationPopup.id);

    if (chargerPlannedRouteOccurence == 0) {
      const heightAnchor = favoriteCharger ? 24 : 20;
        this.drawChargingStationPopup({
          align: PopupAlign.Top, widthAnchor: 0, heightAnchor, chargingStation: chargingStationPopup,
          statusSelector: true, planBtn: true, searched: false
        });
    } else if (chargerPlannedRouteOccurence == 1) {
      this.drawChargingStationPopup({
        align: PopupAlign.Top, widthAnchor: 0, heightAnchor: 80, chargingStation: chargingStationPopup,
        statusSelector: true, planBtn: false, searched: false
      });
    } else {
      this.drawChargingStationPopup({
        align: PopupAlign.Top, widthAnchor: 0, heightAnchor: 61 + (19 * chargerPlannedRouteOccurence),
        chargingStation: chargingStationPopup, statusSelector: true, planBtn: false, searched: false
      });
    }
  }

  // ─── Status Helpers ──────────────────────────────────────────────────

  private changeStatusOpener(htmlEl: string): void {
    const statusOpener = document.querySelector(".status.status-opener");
    if (statusOpener) {
      const icon = statusOpener.querySelector(".status-icon");
      const state = statusOpener.querySelector(".status-state");
      if (icon) icon.remove();
      if (state) state.remove();
      const statusEL = document.createElement('div');
      statusEL.innerHTML = htmlEl;
      for (let i = statusEL.children.length - 1; i >= 0; i--) {
        statusOpener.prepend(statusEL.children[i]);
      }
    }
    if (this.accountService.getIsAuthorized()) {
      this.accountService.setDisabledChargingStations().subscribe();
      this.accountService.setFavoriteChargingStations().subscribe();
    }
    this.statusChanged$.next();
  }

  private getChargerNormalStatusHtmlElements(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "normal",
      this.languageService.languageJSON.Global_Normal, '');
  }

  private getChargerNormalStatusHtmlElementsSelected(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "normal",
      this.languageService.languageJSON.Global_Normal, this.languageService.languageJSON.Map_Popup_Status);
  }

  private getChargerDisabledStatusHtmlElements(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "disabled",
      this.languageService.languageJSON.Map_Popup_Disabled, '');
  }

  private getChargerDisabledStatusHtmlElementsSelected(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "disabled",
      this.languageService.languageJSON.Map_Popup_Disabled, this.languageService.languageJSON.Map_Popup_Status);
  }

  private getChargerFavoriteStatusHtmlElements(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "favorite",
      this.languageService.languageJSON.Map_Popup_Favorite, '');
  }

  private getChargerFavoriteStatusHtmlElementsSelected(): string {
    return this.mapDrawerHelperService.getChargerHtmlElements(this.settingsService.getMap().Skin, "favorite",
      this.languageService.languageJSON.Map_Popup_Favorite, this.languageService.languageJSON.Map_Popup_Status);
  }

  // ─── Utility Methods ─────────────────────────────────────────────────

  private getChargeHereClosestRouteIndex(chargingStation: ChargingStationDetails): number {
    if (this.waypointService.getSelectedDisplayMode() == DisplayMode.RANGE || !this.lastRouteData) {
      return this.waypointService.getWaypointsParams().length;
    }

    const closestRoutePointIdx = this.getClosestRoutePointIdx(this.lastRouteData, chargingStation);
    if (closestRoutePointIdx == -1) {
      return this.waypointService.getWaypointsParams().length;
    }
    return this.countReachedWaypoints(this.lastRouteData, closestRoutePointIdx) + 1;
  }

  // Index of the route point closest to the charging station within 30 km, or -1 if none is.
  private getClosestRoutePointIdx(routeData: Route, chargingStation: ChargingStationDetails): number {
    let closestRoutePointIdx = -1;
    let closestRoutePointDistance = -1;
    for (let i = 0; i < routeData.RoutePoints.length; i++) {
      const actDist = this.utilsService.distanceBetweenCoordinates(
        [chargingStation.lat, chargingStation.lon],
        [routeData.RoutePoints[i].Location.lat, routeData.RoutePoints[i].Location.lng]);
      if (actDist < 30 * 1000 && (closestRoutePointDistance == -1 || actDist < closestRoutePointDistance)) {
        closestRoutePointIdx = i;
        closestRoutePointDistance = actDist;
      }
    }
    return closestRoutePointIdx;
  }

  // Number of waypoint turns the route passes before reaching the given route point.
  private countReachedWaypoints(routeData: Route, routePointIdx: number): number {
    let i = 0;
    let reachedWaypoints = 0;
    while (routeData.Turns[i] && routeData.Turns[i].RoutePointIdxStart <= routePointIdx) {
      if (routeData.Turns[i].TypeIndex == 103 || routeData.Turns[i].TypeIndex == 98 ||
        routeData.Turns[i].TypeIndex == 106) {
        reachedWaypoints++;
      }
      i++;
    }
    return reachedWaypoints;
  }

  private escapeHtml(str: string): string {
    if (!str) return '';
    const div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }
}
